import { array } from 'fp-ts';
import { TimeUnit } from '@mews/enums';
import { createAction, createAsyncActionTypes } from '@mews/framework';
import { AsyncAction, CommanderThunkAction } from '@mews-commander/types';
import { createFetchAction, getFirstDayOfMonthUtc, getLastDayOfMonthUtc } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { fetchProducts } from '@mews-commander/legacy-entities/src/actions/products';
import { setAgeCategories } from '@mews-commander/legacy-entities/src/ducks/ageCategories';
import { fetchCalendarEvents } from '@mews-commander/legacy-entities/src/ducks/calendarEvents';
import { fetchRates } from '@mews-commander/legacy-entities/src/ducks/rates';
import { getServiceTimeUnit } from '@mews-commander/legacy-entities/src/ducks/services';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { prefix } from '../module';
import { RateAdjustmentType } from '../types';
import { getEndUtcForHourlyUnit } from '../utils/time';
import { updatePricingData } from './data';

export const OCCUPANCY_FETCH = prefix('OCCUPANCY_FETCH');
export const [OCCUPANCY_FETCH_STARTED, OCCUPANCY_FETCH_SUCCEEDED, OCCUPANCY_FETCH_FAILED] =
    createAsyncActionTypes(OCCUPANCY_FETCH);

export const PRICING_FETCH = prefix('PRICING_FETCH');
export const [PRICING_FETCH_STARTED, PRICING_FETCH_SUCCEEDED, PRICING_FETCH_FAILED] =
    createAsyncActionTypes(PRICING_FETCH);

export const RATE_MANAGEMENT_DATA_FETCH = prefix('RATE_MANAGEMENT_DATA_FETCH');
export const [
    RATE_MANAGEMENT_DATA_FETCH_STARTED,
    RATE_MANAGEMENT_DATA_FETCH_SUCCEEDED,
    RATE_MANAGEMENT_DATA_FETCH_FAILED,
] = createAsyncActionTypes(RATE_MANAGEMENT_DATA_FETCH);

export const BASE_PRICE_UPDATE = prefix('BASE_PRICE_UPDATE');
export const [BASE_PRICE_UPDATE_STARTED, BASE_PRICE_UPDATE_SUCCEEDED, BASE_PRICE_UPDATE_FAILED] =
    createAsyncActionTypes(BASE_PRICE_UPDATE);

export const PRICING_SET = prefix('PRICING_SET');
export const [PRICING_SET_STARTED, PRICING_SET_SUCCEEDED, PRICING_SET_FAILED] = createAsyncActionTypes(PRICING_SET);

export const PRICING_CLEAR = prefix('PRICING_CLEAR');
export const [PRICING_CLEAR_STARTED, PRICING_CLEAR_SUCCEEDED, PRICING_CLEAR_FAILED] =
    createAsyncActionTypes(PRICING_CLEAR);

export const OCCUPANCY_ADJUSTMENTS_UPDATE = prefix('OCCUPANCY_ADJUSTMENTS_UPDATE');
export const [
    OCCUPANCY_ADJUSTMENTS_UPDATE_STARTED,
    OCCUPANCY_ADJUSTMENTS_UPDATE_SUCCEEDED,
    OCCUPANCY_ADJUSTMENTS_UPDATE_FAILED,
] = createAsyncActionTypes(OCCUPANCY_ADJUSTMENTS_UPDATE);

export const AGE_CATEGORY_ADJUSTMENTS_UPDATE = prefix('AGE_CATEGORY_ADJUSTMENTS_UPDATE');
export const [
    AGE_CATEGORY_ADJUSTMENTS_UPDATE_STARTED,
    AGE_CATEGORY_ADJUSTMENTS_UPDATE_SUCCEEDED,
    AGE_CATEGORY_ADJUSTMENTS_UPDATE_FAILED,
] = createAsyncActionTypes(AGE_CATEGORY_ADJUSTMENTS_UPDATE);

export const ATTRIBUTE_PRICE_ADJUSTMENT_UPDATE = prefix('RESOURCE_CATEGORY_PRICE_UPDATE');
export const [
    ATTRIBUTE_PRICE_ADJUSTMENT_UPDATE_STARTED,
    ATTRIBUTE_PRICE_ADJUSTMENT_UPDATE_SUCCEEDED,
    ATTRIBUTE_PRICE_ADJUSTMENT_UPDATE_FAILED,
] = createAsyncActionTypes(ATTRIBUTE_PRICE_ADJUSTMENT_UPDATE);

export const RATE_DEPENDENCY_UPDATE = prefix('RATE_DEPENDENCY_UPDATE');
export const [RATE_DEPENDENCY_UPDATE_STARTED, RATE_DEPENDENCY_UPDATE_SUCCEEDED, RATE_DEPENDENCY_UPDATE_FAILED] =
    createAsyncActionTypes(RATE_DEPENDENCY_UPDATE);

export const fetchOccupancy =
    ({ startUtc, endUtc, serviceId }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const { serviceComponent } = container();
        const promise = serviceComponent.getOccupancy({ startUtc, endUtc, serviceId });
        return dispatch(createFetchAction(OCCUPANCY_FETCH, promise));
    };

export const fetchPricing =
    ({ filterFirstTimeUnitStartUtc, filterLastTimeUnitStartUtc, rateId, productId, extent = {} }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const { pricingComponent } = container();
        const promise = pricingComponent.get({
            productId,
            rateId,
            filterFirstTimeUnitStartUtc,
            filterLastTimeUnitStartUtc,
            extent,
        });
        return dispatch(createFetchAction(PRICING_FETCH, promise));
    };

export const fetchRateManagementData =
    ({ productId, rateId, serviceId, startUtc, endUtc, extent }: $TSFixMe, { product, occupancy }: $TSFixMe = {}) =>
    (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();
        const enterpriseId = getCurrentEnterpriseId();
        const serviceTimeUnit = getServiceTimeUnit(state, { id: serviceId });
        const timezone: string = getCurrentEnterpriseIanaTimeZoneIdentifier(state);
        let commonStartUtc;
        let commonEndUtc;
        let calendarEventsEndUtc;

        switch (serviceTimeUnit) {
            case TimeUnit.Month:
                /**
                 * Pricing and occupancy are using for start and end UTCs first day of month.
                 * Calendar events are using for start UTC first day of month and for end UTC last day of month.
                 */
                commonStartUtc = getFirstDayOfMonthUtc(startUtc, timezone);
                commonEndUtc = getFirstDayOfMonthUtc(endUtc, timezone);
                calendarEventsEndUtc = getLastDayOfMonthUtc(endUtc, timezone);
                break;
            case TimeUnit.Hour:
                commonStartUtc = startUtc;
                commonEndUtc = getEndUtcForHourlyUnit(endUtc, timezone);
                calendarEventsEndUtc = getEndUtcForHourlyUnit(endUtc, timezone);
                break;
            default:
                commonStartUtc = startUtc;
                commonEndUtc = endUtc;
                calendarEventsEndUtc = endUtc;
        }

        const commonFetchActions = [
            dispatch(
                fetchPricing({
                    filterFirstTimeUnitStartUtc: commonStartUtc,
                    filterLastTimeUnitStartUtc: commonEndUtc,
                    productId,
                    rateId,
                    extent,
                })
            ).then(({ value }: $TSFixMe) => {
                const {
                    attributeType,
                    basePrice,
                    attributePrices,
                    ageCategories,
                    capacityOffsetAdjustments,
                    rateAdjustment,
                } = value;
                if (ageCategories !== null && array.isNonEmpty(ageCategories)) {
                    // @ts-expect-error $TSFixMeError: Argument of type 'NonEmptyArray<unknown>' is not assignable to parameter of type 'AgeCategory[]'.
                    dispatch(setAgeCategories(ageCategories));
                }
                dispatch(
                    updatePricingData({
                        attributeType,
                        basePrice,
                        attributePrices,
                        capacityOffsetAdjustments,
                        rateAdjustment,
                    })
                );
                return rateAdjustment?.discriminator === RateAdjustmentType.DependentRateAdjustment
                    ? dispatch(fetchRates({ rateIds: [rateAdjustment.value.baseRateId], serviceIds: [serviceId] }))
                    : Promise.resolve();
            }),
            dispatch(fetchCalendarEvents(commonStartUtc, calendarEventsEndUtc, enterpriseId)),
        ];

        const additionalFetchActions = [
            product
                ? dispatch(
                      fetchProducts({
                          enterpriseIds: [enterpriseId],
                          productIds: [productId],
                          serviceIds: [serviceId],
                      })
                  )
                : Promise.resolve(),
            occupancy
                ? dispatch(fetchOccupancy({ startUtc: commonStartUtc, endUtc: commonEndUtc, serviceId }))
                : Promise.resolve(),
        ];

        return dispatch(
            createAction(RATE_MANAGEMENT_DATA_FETCH, Promise.all([...commonFetchActions, ...additionalFetchActions]))
        );
    };

export const updateBasePrice =
    ({
        productId,
        rateId,
        filterFirstTimeUnitStartUtc,
        filterLastTimeUnitStartUtc,
        value,
        currencyCode,
        taxValues,
        taxExemptionReasonCode,
        taxExemptionLegalReference,
    }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const { pricingComponent } = container();
        const promise = pricingComponent.updateBasePrice({
            productId,
            rateId,
            filterFirstTimeUnitStartUtc,
            filterLastTimeUnitStartUtc,
            value,
            currencyCode,
            taxValues,
            taxExemptionReasonCode,
            taxExemptionLegalReference,
        });
        return dispatch(createFetchAction(BASE_PRICE_UPDATE, promise));
    };

export const setPricing =
    ({
        attributeType,
        attributeId,
        adjustmentFirstTimeUnitStartUtc,
        adjustmentLastTimeUnitStartUtc,
        days,
        hours,
        value,
        productId,
        rateId,
        filterFirstTimeUnitStartUtc,
        filterLastTimeUnitStartUtc,
    }: $TSFixMe): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const { pricingComponent } = container();
        const promise = pricingComponent.set({
            attributeType,
            attributeId,
            adjustmentFirstTimeUnitStartUtc,
            adjustmentLastTimeUnitStartUtc,
            days,
            hours,
            value,
            productId,
            rateId,
            filterFirstTimeUnitStartUtc,
            filterLastTimeUnitStartUtc,
        });
        return dispatch(createFetchAction(PRICING_SET, promise));
    };

export const clearPricing =
    ({
        attributeType,
        attributeId,
        adjustmentFirstTimeUnitStartUtc,
        adjustmentLastTimeUnitStartUtc,
        days,
        hours,
        productId,
        rateId,
        filterFirstTimeUnitStartUtc,
        filterLastTimeUnitStartUtc,
    }: $TSFixMe): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const { pricingComponent } = container();
        const promise = pricingComponent.clear({
            attributeType,
            attributeId,
            adjustmentFirstTimeUnitStartUtc,
            adjustmentLastTimeUnitStartUtc,
            days,
            hours,
            productId,
            rateId,
            filterFirstTimeUnitStartUtc,
            filterLastTimeUnitStartUtc,
        });
        return dispatch(createFetchAction(PRICING_CLEAR, promise));
    };

export const updateOccupancyAdjustments =
    ({
        negativeOccupancyAdjustment,
        extraOccupancyAdjustment,
        attributeType,
        productId,
        rateId,
        filterFirstTimeUnitStartUtc,
        filterLastTimeUnitStartUtc,
    }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const { pricingComponent } = container();
        const promise = pricingComponent.updateCapacityOffsets({
            ageCategoryOffsetAdjustments: [],
            negativeOccupancyAdjustment,
            extraOccupancyAdjustment,
            attributeType,
            productId,
            rateId,
            filterFirstTimeUnitStartUtc,
            filterLastTimeUnitStartUtc,
        });
        return dispatch(createFetchAction(OCCUPANCY_ADJUSTMENTS_UPDATE, promise));
    };

export const updateAgeCategoryAdjustments =
    ({
        ageCategoryOffsetAdjustments,
        attributeType,
        productId,
        rateId,
        filterFirstTimeUnitStartUtc,
        filterLastTimeUnitStartUtc,
    }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const { pricingComponent } = container();
        const promise = pricingComponent.updateCapacityOffsets({
            ageCategoryOffsetAdjustments,
            negativeOccupancyAdjustment: null,
            extraOccupancyAdjustment: null,
            attributeType,
            productId,
            rateId,
            filterFirstTimeUnitStartUtc,
            filterLastTimeUnitStartUtc,
        });
        return dispatch(createFetchAction(AGE_CATEGORY_ADJUSTMENTS_UPDATE, promise));
    };

export const updateDependency =
    ({
        attributeType,
        attributeId,
        baseAttributeId,
        productId,
        rateId,
        filterFirstTimeUnitStartUtc,
        filterLastTimeUnitStartUtc,
        relativeValue,
        absoluteValue,
    }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const { pricingComponent } = container();
        const promise = pricingComponent.updateDependency({
            attributeType,
            attributeId,
            baseAttributeId,
            productId,
            rateId,
            filterFirstTimeUnitStartUtc,
            filterLastTimeUnitStartUtc,
            relativeValue,
            absoluteValue,
        });
        return dispatch(createFetchAction(ATTRIBUTE_PRICE_ADJUSTMENT_UPDATE, promise));
    };

export const updateRateDependency =
    ({
        absoluteAdjustment,
        relativeAdjustment,
        productId,
        rateId,
        attributeType,
        filterFirstTimeUnitStartUtc,
        filterLastTimeUnitStartUtc,
    }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const { pricingComponent } = container();
        const promise = pricingComponent.updateRateDependency({
            absoluteAdjustment,
            relativeAdjustment,
            productId,
            rateId,
            attributeType,
            filterFirstTimeUnitStartUtc,
            filterLastTimeUnitStartUtc,
        });
        return dispatch(createFetchAction(RATE_DEPENDENCY_UPDATE, promise));
    };
